import { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, auth, ensureUserDocument, signOutUser } from '../services/auth.service';
import { ensureAdminClaim } from '../../../services/firebase/functions';

const buildUser = async (firebaseUser) => {
  // Force a token refresh so Firestore security rules see the latest
  // emailVerified state (otherwise the token may still contain false after
  // the user clicked the verification link).
  await firebaseUser.getIdToken(true);

  // La callable crée ou migre le profil et sa réservation atomiquement.
  const userData = await ensureUserDocument(firebaseUser.uid);

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    emailVerified: firebaseUser.emailVerified,
    ...userData,
    // Le photoURL de Firebase Auth prime sur celui du doc Firestore : c'est la
    // source la plus fraîche (updateUserProfilePhoto met Auth à jour en
    // premier, et un doc créé sans photoURL — ex. ancien compte Google — ne
    // doit pas masquer l'avatar Auth avec son `null`).
    photoURL: firebaseUser.photoURL ?? userData?.photoURL ?? null
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // disposed = composant démonté ; eventSeq = chaque événement auth
    // invalide les continuations asynchrones des événements précédents
    // (un retry en vol ne doit jamais écraser l'état d'un sign-in/out plus
    // récent, ni setState après unmount).
    let disposed = false;
    let eventSeq = 0;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const seq = ++eventSeq;
      const isCurrent = () => !disposed && seq === eventSeq;

      if (firebaseUser) {
        // Race d'inscription beta : redeemBetaCode / ensureUserProfile peut
        // encore être en vol dans un autre bootstrap. On retente buildUser avant de laisser le
        // profil à null — sinon la sidebar restait figée sur les fallbacks
        // 'U' / 'User'.
        let builtUser = null;
        let lastError = null;
        for (let attempt = 0; attempt < 3 && !builtUser; attempt++) {
          try {
            builtUser = await buildUser(firebaseUser);
          } catch (err) {
            lastError = err;
            if (attempt < 2) {
              await sleep(1500);
            }
          }
          if (!isCurrent()) return;
        }

        if (!builtUser) {
          console.error('[AUTH] buildUser error:', lastError);
          setUser(null);
          setLoading(false);
          return;
        }

        // Ensure admin claim is set for configured admin emails
        let isAdmin = false;
        try {
          const result = await ensureAdminClaim();
          const { isAdmin: claimIsAdmin, claimSet } = result.data;

          if (claimSet) {
            await firebaseUser.getIdToken(true);
          }

          const tokenResult = await firebaseUser.getIdTokenResult();
          isAdmin = tokenResult.claims.admin === true;

          // Si le callable n’a pas pu confirmer mais que le token contient déjà le claim,
          // on garde le statut admin (fallback CORS / réseau).
          if (!isAdmin && claimIsAdmin === true) {
            isAdmin = true;
          }
        } catch (claimErr) {
          console.warn('[AUTH] ensureAdminClaim failed:', claimErr);
          const tokenResult = await firebaseUser.getIdTokenResult();
          isAdmin = tokenResult.claims.admin === true;
        }

        builtUser.isAdmin = isAdmin;

        if (!isCurrent()) return;
        setUser(builtUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      disposed = true;
      unsubscribe();
    };
  }, []);

  const refreshUser = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;
    await firebaseUser.reload();
    setUser(await buildUser(firebaseUser));
  }, []);

  const signOut = useCallback(async () => {
    return signOutUser();
  }, []);

  // Alias for consistency across components
  const logout = signOut;

  return { user, loading, signOut, logout, refreshUser };
};